import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { useAuth } from "../../firebase/AuthProvider";
import { useChatConnection } from "../../contexts/ChatConnectionContext";
import { ChatReadState, Conversation, getMyConversations } from "../../services/chatService/ChatService";

export function useChatReadCountReconciliation(conversations: Conversation[], setConversations: Dispatch<SetStateAction<Conversation[]>>) {
    const { currentUser } = useAuth();
    const { connection } = useChatConnection();
    const currentConversations = useRef(conversations);
    currentConversations.current = conversations;

    useEffect(() => {
        if (!connection || !currentUser) return;
        let disposed = false;
        const versions = new Map<number, number>();
        const receive = async (state: ChatReadState) => {
            if (state.threadRootMessageId || state.conversationId <= 0) return;
            // Activity-only reads use the same hub payload as message reads.
            // Query the authoritative count rather than clearing message dots.
            const snapshot = currentConversations.current.find(item => item.id === state.conversationId);
            if (!snapshot) return;
            const version = (versions.get(state.conversationId) ?? 0) + 1;
            versions.set(state.conversationId, version);
            try {
                const token = await currentUser.getIdToken();
                if (disposed) return;
                const refreshed = (await getMyConversations(token)).find(item => item.id === state.conversationId);
                if (disposed || versions.get(state.conversationId) !== version || !refreshed || refreshed.unreadCount == null) return;
                setConversations(previous => {
                    if (disposed || versions.get(state.conversationId) !== version) return previous;
                    return previous.map(item => item === snapshot ? { ...item, unreadCount: refreshed.unreadCount } : item);
                });
            } catch (error) {
                console.error("Unable to refresh chat unread count", error);
            }
        };
        connection.on("ReceiveChatReadState", receive);
        return () => {
            disposed = true;
            connection.off("ReceiveChatReadState", receive);
        };
    }, [connection, currentUser, setConversations]);
}
